import { forwardRef, HTMLAttributes } from 'react';
import { ButtonIcon } from '@backstage/ui';
import { RiCloseLine } from '@remixicon/react';

import styles from './Chip.module.css';

export interface ChipProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> {
  label: string;
  className?: string;
  labelClassName?: string;
  /** Shows a remove button that calls this function. */
  onDelete?: () => void;
  /** Accessible name of the remove button; defaults to "Remove <label>". */
  deleteLabel?: string;
}

/**
 * A single pill-shaped label, for example a lifecycle or a tag on a card.
 *
 * For a list of tags the user can remove or select, use the Backstage UI
 * `TagGroup` and `Tag`, which add keyboard navigation across the tags.
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    { label, className, labelClassName, onDelete, deleteLabel, ...extraProps },
    ref,
  ) => (
    <div
      ref={ref}
      className={[styles.chip, 'xlrt-Chip', className]
        .filter(Boolean)
        .join(' ')}
      {...extraProps}
    >
      <span
        className={[styles.label, 'xlrt-ChipLabel', labelClassName]
          .filter(Boolean)
          .join(' ')}
      >
        {label}
      </span>
      {onDelete && (
        <ButtonIcon
          variant="tertiary"
          size="small"
          icon={<RiCloseLine aria-hidden="true" />}
          aria-label={deleteLabel ?? `Remove ${label}`}
          onPress={onDelete}
          className={styles.deleteButton}
        />
      )}
    </div>
  ),
);

Chip.displayName = 'Chip';
